import { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { updateRemittanceAllocationsThunk } from '../../../../redux/services/cashApplicationService';
import type {
  AppliedInvoice,
  ConfidenceFactor,
  RemittanceRecord,
  MatchedInvoice,
  MatchMethod,
} from '@dill/dill-shared';
import { ConfidenceBadge } from '../components/ConfidenceBadge';
import { ChannelBadge } from '../components/ChannelBadge';
import { SettlementBadge } from '../components/SettlementBadge';
import { AdjustAllocationModal } from './AdjustAllocationModal';
import { DillTable, DillButton } from '../../../general/Dill';
import type { DillColumn } from '../../../general/Dill';
import { Eye, CaretDown, Trash } from 'phosphor-react';
import { openDillToast } from '../../../../utils/helpers';
import { formatCurrency, formatDate, formatDateTime } from '../utils';
import { useDeleteRemittance } from './useDeleteRemittance';

const MATCH_METHOD_LABELS: Record<MatchMethod, { label: string; className: string }> = {
  EXACT_DOC_NUMBER: { label: 'Exact Doc#', className: 'bg-emerald-50 text-emerald-700' },
  EXACT_DOC_NUMBER_AMOUNT: { label: 'Exact Match', className: 'bg-emerald-100 text-emerald-800' },
  EXACT_PO: { label: 'Exact PO', className: 'bg-blue-50 text-blue-700' },
  EXACT_JOB_NAME: { label: 'Exact Job', className: 'bg-emerald-50 text-emerald-700' },
  EXACT_AMOUNT: { label: 'Exact Amt', className: 'bg-cyan-50 text-cyan-700' },
  FUZZY: { label: 'Fuzzy', className: 'bg-gray-100 text-gray-600' },
  VECTOR_SEARCH: { label: 'AI', className: 'bg-violet-50 text-violet-700' },
  // Grey, not green: the advice named no invoice and we chose these ourselves.
  LAST_STATEMENT: { label: 'From statement', className: 'bg-gray-100 text-gray-600' },
  OPEN_INVOICES: { label: 'Oldest open', className: 'bg-gray-100 text-gray-600' },
  MANUAL: { label: 'Manual', className: 'bg-amber-50 text-amber-700' },
};

// Portal-based hover tooltip for matched invoice details
const ExceptionMatchTooltip = ({
  invoices,
  pendingAllocs,
  children,
}: {
  invoices: MatchedInvoice[];
  pendingAllocs?: AppliedInvoice[];
  children: (props: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    ref: React.RefObject<HTMLDivElement>;
  }) => React.ReactNode;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHideTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleTriggerMouseEnter = () => {
    clearHideTimeout();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const PANEL_W = 520;
      const left =
        rect.right + PANEL_W + 8 > window.innerWidth ? rect.left - PANEL_W - 4 : rect.right + 8;
      setPosition({ top: rect.top, left });
    }
    setIsHovered(true);
  };

  const handleTooltipMouseEnter = () => clearHideTimeout();

  const handleMouseLeave = () => {
    clearHideTimeout();
    timeoutRef.current = setTimeout(() => setIsHovered(false), 150);
  };

  useEffect(() => {
    return () => clearHideTimeout();
  }, []);

  // Pull the panel back inside the viewport once its real height is known. Anchored to the row's
  // top, the last rows' panels run off the bottom of the window, and a hover target has no scroll
  // of its own — the invoices below the fold cannot be read at all.
  useLayoutEffect(() => {
    if (!isHovered || !panelRef.current) {
      return;
    }
    const { height } = panelRef.current.getBoundingClientRect();
    const VIEWPORT_MARGIN = 8;
    const lowestTop = window.innerHeight - height - VIEWPORT_MARGIN;
    setPosition((current) => {
      const top = Math.max(VIEWPORT_MARGIN, Math.min(current.top, lowestTop));
      return top === current.top ? current : { ...current, top };
    });
  }, [isHovered]);

  const displayInvoices = pendingAllocs?.length
    ? invoices.map((inv) => {
        const pendingInv = pendingAllocs.find((p) => p.invoiceId === inv.invoiceId);
        return pendingInv ? { ...inv, suggestedAmount: pendingInv.amountApplied } : inv;
      })
    : invoices;

  return (
    <>
      {children({
        onMouseEnter: handleTriggerMouseEnter,
        onMouseLeave: handleMouseLeave,
        ref: triggerRef,
      })}
      {isHovered &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed bg-white border border-gray-200 shadow-lg rounded-xl z-[99999] w-[520px]"
            style={{ top: `${position.top}px`, left: `${position.left}px` }}
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => e.stopPropagation()}>
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Possible Matches ({displayInvoices.length})
              </p>
            </div>
            <div className="max-h-[280px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                    <th className="text-left px-3 py-1.5">Doc #</th>
                    <th className="text-right px-3 py-1.5">Invoice</th>
                    <th className="text-right px-3 py-1.5">Balance</th>
                    <th className="text-right px-3 py-1.5">Suggested</th>
                    <th className="text-center px-3 py-1.5">Confidence</th>
                    <th className="text-left px-3 py-1.5">Method</th>
                  </tr>
                </thead>
                <tbody>
                  {displayInvoices.map((inv) => {
                    const mc = MATCH_METHOD_LABELS[inv.matchMethod] ?? MATCH_METHOD_LABELS.FUZZY;
                    return (
                      <tr
                        key={inv.invoiceId}
                        className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                        <td className="px-3 py-1.5 font-medium text-gray-900">{inv.docNumber}</td>
                        <td className="px-3 py-1.5 text-right text-gray-600">
                          {formatCurrency(inv.invoiceAmount)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-600">
                          {formatCurrency(inv.openBalance)}
                        </td>
                        <td className="px-3 py-1.5 text-right font-semibold text-gray-900">
                          {formatCurrency(inv.suggestedAmount ?? 0)}
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <span
                            className={`text-[11px] font-medium ${
                              inv.confidence >= 0.75
                                ? 'text-emerald-600'
                                : inv.confidence >= 0.5
                                ? 'text-amber-600'
                                : 'text-red-500'
                            }`}>
                            {Math.round(inv.confidence * 100)}%
                          </span>
                        </td>
                        <td className="px-3 py-1.5">
                          <span
                            className={
                              'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ' +
                              mc.className
                            }>
                            {mc.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

interface ExceptionRow {
  id: string;
  paymentDate: string;
  channel: RemittanceRecord['channel'];
  payerName: string | null;
  checkNumber: string | null;
  amount: number;
  matchConfidence: number | null;
  matchConfidenceFactors: ConfidenceFactor[];
  matchedInvoices: RemittanceRecord['matchedInvoices'];
  memo: string | null;
  settlementStatus: RemittanceRecord['settlementStatus'];
  createdAt: Date;
  record: RemittanceRecord;
}

export const ExceptionsQueue = () => {
  const dispatch = useAppDispatch();
  const {
    exceptionItems: items,
    openInvoices: allOpenInvoices,
    pendingAllocations,
    loadingList,
  } = useAppSelector((state) => state.cashApplication);
  const loading = loadingList.length > 0;
  const user = useAppSelector((state) => state.auth.user);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const supplierId = currentSupplier?.id;

  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [allocations, setAllocations] = useState<Record<string, number>>({});
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [savingAllocation, setSavingAllocation] = useState(false);

  const selectedItemRecord = items.find((i) => i.id === selectedItem) ?? null;

  const openInvoices = useMemo(() => {
    if (!selectedItemRecord) return [];

    const customerInvoices = allOpenInvoices.filter(
      (inv) =>
        selectedItemRecord.matchedInvoices.map((ii) => ii.invoiceId).includes(inv.id) ||
        !selectedItemRecord?.buyerId
    );
    if (!invoiceSearch) return customerInvoices;
    const search = invoiceSearch.toLowerCase();
    return customerInvoices.filter(
      (inv) =>
        inv.docNumber?.toLowerCase().includes(search) ||
        inv.customerName?.toLowerCase().includes(search) ||
        inv.poNumber?.toLowerCase().includes(search)
    );
  }, [selectedItemRecord, invoiceSearch, allOpenInvoices]);

  const totalAllocated = useMemo(
    () => Object.values(allocations).reduce((sum, val) => sum + (val || 0), 0),
    [allocations]
  );

  /**
   * The operator's agreement to whatever this allocation leaves over.
   *
   * Saved onto the record rather than kept here, because the post happens in 1-Click Review and
   * refuses a remainder nobody has agreed to. Without this the allocation saved here would land in
   * that queue already blocked, with the only control that could unblock it in another modal.
   */
  const [postRemainderAsCredit, setPostRemainderAsCredit] = useState(false);

  const handleSelectItem = useCallback(
    (id: string) => {
      setSelectedItem(id);
      setInvoiceSearch('');
      const item = items.find((i) => i.id === id);
      setPostRemainderAsCredit(item?.openCreditApproved === true);
      const pending = pendingAllocations[id];
      if (pending && pending.length > 0) {
        const allocs: Record<string, number> = {};
        pending.forEach((inv) => {
          allocs[inv.invoiceId] = inv.amountApplied;
        });
        setAllocations(allocs);
      } else {
        const initialAllocs: Record<string, number> = {};
        if (item) {
          item.matchedInvoices.forEach((inv) => {
            initialAllocs[inv.invoiceId] = inv.suggestedAmount ?? inv.openBalance;
          });
        }
        setAllocations(initialAllocs);
      }
    },
    [pendingAllocations, items]
  );

  const handleAllocationChange = (invoiceId: string, value: string) => {
    const num = parseFloat(value) || 0;
    setAllocations((prev) => ({ ...prev, [invoiceId]: num }));
  };

  const closeModal = () => {
    setSelectedItem(null);
    setAllocations({});
    setInvoiceSearch('');
    setPostRemainderAsCredit(false);
  };

  /**
   * Save the allocation, and nothing more — the same contract as Awaiting Bank's edit.
   *
   * Posting does not belong here. A record is in Exceptions because its matchStatus is PENDING,
   * and saving an allocation is exactly what clears that: the server sets AUTO_MATCHED, so the
   * payment leaves this queue for 1-Click Review, which is where posting is reviewed and done.
   * Offering Apply & Post here meant two routes to the ERP with two sets of guards.
   */
  const handleSaveAllocation = async () => {
    if (!selectedItemRecord || !supplierId) {
      return;
    }
    setSavingAllocation(true);
    try {
      const result = await dispatch(
        updateRemittanceAllocationsThunk({
          supplierId,
          remittanceId: selectedItemRecord.id,
          allocations: Object.entries(allocations)
            .filter(([, amount]) => amount > 0)
            .map(([invoiceId, amount]) => {
              const invoice = allOpenInvoices.find((candidate) => candidate.id === invoiceId);
              const matched = selectedItemRecord.matchedInvoices.find(
                (candidate) => candidate.invoiceId === invoiceId
              );
              return {
                invoiceId,
                docNumber: invoice?.docNumber ?? matched?.docNumber ?? '',
                amount,
                invoiceAmount: invoice?.amount ?? matched?.invoiceAmount ?? null,
                openBalance: invoice?.balance ?? matched?.openBalance ?? null,
              };
            }),
          postRemainderAsCredit,
        })
      );

      if (result.meta.requestStatus === 'fulfilled') {
        const { allocated, unapplied } = result.payload as {
          allocated: number;
          unapplied: number;
        };
        openDillToast({
          message:
            allocated > 0
              ? unapplied > 0
                ? `Allocation saved and moved to 1-Click Review. ${formatCurrency(
                    unapplied
                  )} is still unallocated and will post as unapplied cash.`
                : 'Allocation saved. The payment has moved to 1-Click Review to be posted.'
              : postRemainderAsCredit
              ? `Saved. The whole ${formatCurrency(
                  unapplied
                )} will post as open credit on the account — the payment has moved to 1-Click Review.`
              : 'Allocation cleared. The payment stays here until it is allocated.',
          type: 'SUCCESS',
        });
        closeModal();
      } else {
        openDillToast({
          message: (result.payload as string) ?? 'Failed to save the allocation.',
          type: 'ERROR',
        });
      }
    } finally {
      setSavingAllocation(false);
    }
  };

  // Nothing posts from this queue, so there is no "fully accounted for" rule to enforce — a
  // partial allocation is a legitimate save, and the remainder is answered in 1-Click Review.
  // Approving the whole payment as open credit is a save too, and the only one available when the
  // customer has no open invoice to allocate against.
  const hasAllocations = totalAllocated > 0 || postRemainderAsCredit;

  // Table rows
  const rows: ExceptionRow[] = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        paymentDate: item.paymentDate,
        channel: item.channel,
        payerName: item.payerName,
        checkNumber: item.checkNumber,
        amount: item.amount,
        matchConfidence: item.matchConfidence,
        matchConfidenceFactors: item.matchConfidenceFactors ?? [],
        matchedInvoices: item.matchedInvoices,
        memo: item.memo,
        settlementStatus: item.settlementStatus,
        createdAt: item.createdAt,
        record: item,
      })),
    [items]
  );

  const confirmDelete = useDeleteRemittance(supplierId);

  const columns: DillColumn<ExceptionRow>[] = useMemo(
    () => [
      {
        key: 'createdAt',
        label: 'Created',
        width: 130,
        sortable: true,
        getSortValue: (row) => new Date(row.createdAt).getTime(),
        render: (_v, row) => (
          <span className="text-xs text-gray-500">{formatDateTime(row.createdAt)}</span>
        ),
      },
      {
        key: 'paymentDate',
        label: 'Payment Date',
        width: 120,
        sortable: true,
        getSortValue: (row) => new Date(row.paymentDate).getTime(),
        render: (_v, row) => (
          <span className="text-xs text-gray-700">{formatDate(row.paymentDate)}</span>
        ),
      },
      {
        key: 'channel',
        label: 'Channel',
        width: 90,
        sortable: false,
        render: (_v, row) => <ChannelBadge channel={row.channel} />,
      },
      {
        // A settled exception is a real case: the bank confirmed the money, but the allocation
        // was too uncertain to post, so it still needs a human.
        key: 'settlementStatus',
        label: 'Status',
        width: 190,
        sortable: false,
        filterable: true,
        render: (_v, row) => <SettlementBadge status={row.settlementStatus} />,
      },
      {
        key: 'payerName',
        label: 'Payer',
        width: 160,
        sortable: true,
        getSearchValue: (row) =>
          `${row.payerName ?? ''} ${row.checkNumber ?? ''} ${row.memo ?? ''}`.toLowerCase(),
        render: (_v, row) => (
          <div>
            <p className="text-xs font-medium text-gray-900 truncate">{row.payerName}</p>
            {row.checkNumber && (
              <p className="text-[11px] text-gray-400 mt-0.5">Check #{row.checkNumber}</p>
            )}
          </div>
        ),
      },
      {
        key: 'amount',
        label: 'Amount',
        width: 100,
        numeric: true,
        sortable: true,
        render: (_v, row) => (
          <span className="text-xs font-semibold text-gray-900">{formatCurrency(row.amount)}</span>
        ),
      },
      {
        key: 'matchConfidence',
        label: 'Confidence',
        width: 90,
        sortable: true,
        headerAlign: 'center',
        render: (_v, row) => (
          <div className="flex justify-center">
            <ConfidenceBadge
              confidence={row.matchConfidence}
              showLabel={false}
              factors={row.matchConfidenceFactors}
            />
          </div>
        ),
      },
      {
        key: 'matchedInvoices',
        label: 'Possible Matches',
        width: 220,
        sortable: false,
        render: (_v, row) => {
          if (row.matchedInvoices.length === 0) {
            return <span className="text-xs text-gray-300">No matches</span>;
          }

          const pending = pendingAllocations[row.id];
          const invoices = pending ?? row.matchedInvoices;
          const totalSuggested = invoices.reduce(
            (sum, inv) =>
              sum +
              ('amountApplied' in inv
                ? (inv as AppliedInvoice).amountApplied
                : (inv as MatchedInvoice).suggestedAmount ?? 0),
            0
          );

          return (
            <ExceptionMatchTooltip
              invoices={row.matchedInvoices}
              pendingAllocs={pending ?? undefined}>
              {({ onMouseEnter, onMouseLeave, ref }) => (
                <div
                  ref={ref}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  className="flex items-center gap-1.5 cursor-pointer group w-full">
                  <div className="flex flex-col items-center gap-1 w-full">
                    <div className="flex items-center gap-1">
                      <CaretDown
                        className="text-gray-400 group-hover:text-orange-500 transition-colors"
                        size={12}
                      />
                      <span className="text-[11px] text-gray-500">
                        {invoices.length} match{invoices.length !== 1 ? 'es' : ''} &middot;{' '}
                        {formatCurrency(totalSuggested)}
                      </span>
                      {pending && (
                        <span className="text-[11px] font-medium text-amber-700 bg-amber-100 rounded px-1 py-0">
                          Edited
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </ExceptionMatchTooltip>
          );
        },
      },
      {
        // Delete lives here rather than in the table's own rowActions, so both actions share one
        // sticky cell instead of docking as two separate blocks against the right edge.
        key: 'actions',
        label: '',
        width: 196,
        sortable: false,
        stickyRight: true,
        render: (_v, row) => (
          <div className="flex items-center justify-end gap-1.5">
            <DillButton
              onClick={() => handleSelectItem(row.id)}
              label="Review & Match"
              variant="primary"
              icon={<Eye size={14} />}
            />
            <DillButton
              onClick={() => confirmDelete(row.record)}
              variant="icon-red"
              icon={<Trash size={14} />}
            />
          </div>
        ),
      },
    ],
    [pendingAllocations, handleSelectItem, confirmDelete]
  );

  // List view
  // Empty is handled by the table's own emptyMessage rather than by returning early — see the
  // same note in OneClickReviewQueue.

  return (
    <>
      <DillTable<ExceptionRow>
        columns={columns}
        data={rows}
        rowKey={(row) => row.id}
        search={true}
        searchPlaceholder="Search payer, check #, memo..."
        striped={false}
        hoverVariant="default"
        emptyMessage="No exceptions — every payment is matched or awaiting 1-click review."
        pagination={rows.length > 20}
        pageSize={20}
        loading={loading}
        scrollX
      />

      {/*
        Save is the only action, as in Awaiting Bank's edit. Omitting the post handlers is the
        enforcement — the modal hides what it is not given.
      */}
      <AdjustAllocationModal
        adjustItem={selectedItemRecord}
        title="Review & match"
        allocations={allocations}
        onAllocationChange={handleAllocationChange}
        totalAllocated={totalAllocated}
        filteredOpenInvoices={openInvoices}
        invoiceSearch={invoiceSearch}
        onInvoiceSearchChange={setInvoiceSearch}
        onClose={closeModal}
        onSaveOnly={handleSaveAllocation}
        saveLabel={savingAllocation ? 'Saving...' : 'Save allocation'}
        isValidAllocation={false}
        hasAllocations={hasAllocations && !savingAllocation}
        onAllocationsReset={() => setAllocations({})}
        onPostRemainderChange={setPostRemainderAsCredit}
        postRemainderAsCredit={postRemainderAsCredit}
      />
    </>
  );
};
